import { utils } from '@digital-commerce/shared-utils'
import {
  pipe,
  evolve,
  path,
  map,
  ifElse,
  hasPath,
  prop,
  filter,
  includes,
  isNil,
  memoizeWith
} from 'ramda'
import { formatCurrency } from '../../../utils/currency'
import { getCacheKey, getItemWithTags } from '../../../utils/cart'

const { getHSService, getMonthlyItemPrice } = utils.cart

export const getLWCService = memoizeWith(getCacheKey('LWC'), (cart) => {
  if (!cart?.root?.items) return undefined

  return pipe(
    getHSService(['lwc']),
    evolve({
      items: map((id) => path(['items', id], cart))
    })
  )(cart)
})

export const isLWCMigrationEnabled = prop('lwcMigration')

export const getContentTitleDisplayName = ifElse(
  hasPath(['content', 'title']),
  path(['content', 'title']),
  ifElse(
    hasPath(['productOffering', 'displayName']),
    path(['productOffering', 'displayName']),
    path(['productOffering', 'name'])
  )
)

const buildSubItemsList = (subItems, lang, isQuantityDisplayed = true) => {
  const mapper = (featureItem) => {
    const name = getContentTitleDisplayName(featureItem)
    const recurringDuration = path(['itemPrice', 'recurring', 0, 'recurringDuration'], featureItem)
    const monthlyItemPrice = formatCurrency(getMonthlyItemPrice(featureItem).subTotal, lang)
    const price = recurringDuration === 1 ? monthlyItemPrice : `${monthlyItemPrice}/mo.`
    const quantity = `${featureItem?.quantity} `

    return {
      id: featureItem.id,
      name: isQuantityDisplayed ? `${quantity}${name}` : name,
      price
    }
  }

  return subItems?.map(mapper)
}

const getHardware = pipe(prop('items'), filter(pipe(prop('tags'), includes('hardware'))))

export const getAdditionalCharges = pipe(
  getItemWithTags(['lwc', 'fee']),
  filter((item) => getMonthlyItemPrice(item).amount > 0)
)

export const buildAdditionalChargesList = (cart, lang) => {
  return buildSubItemsList(getAdditionalCharges(Object.values(cart?.items)), lang, false)
}

export const getAdditionalHardware = pipe(
  getHardware,
  filter((item) => getMonthlyItemPrice(item).amount > 0)
)

export const getIncludedHardware = pipe(
  getHardware,
  filter((item) => getMonthlyItemPrice(item).amount === 0)
)

export const buildIncludedHardwareList = (service, lang) => {
  return buildSubItemsList(getIncludedHardware(service), lang)
}

export const buildAdditionalHardwareList = (service, lang) => {
  return buildSubItemsList(getAdditionalHardware(service), lang)
}

export const shouldRender = (cart, featureFlags) => {
  const lwcService = getLWCService(cart)
  return isLWCMigrationEnabled(featureFlags) && !isNil(lwcService)
}
